import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'react-toastify'

import {
  EAgentKeys,
  EWebScrapeStatus,
  TAgent,
  TUpdateAgent,
} from '~/features/agents/models'
import { getDefaultAgentData, queryKeys } from '~/features/agents/utils'
import { queryKeys as preChatFormQueryKeys } from '~/features/preChatForms/utils'
import { EHttpService, EServerPath } from '~/models/api'
import HttpService from '~/services/http'
import { HttpServiceBaseResponse } from '~/services/http/models'
import { store } from '~/store'
import pathBuilder from '~/utils/api'

type TUpdateAgentData = {
  agentId?: string
  hideErrorToast?: boolean
  hideToast?: boolean
  payload: Partial<TUpdateAgent>
  skipInvalidation?: boolean
}

/**
 * This is the consolidated agent update mutation which contains phone settings, chat settings, email settings.
 * https://github.com/heylibby/heyLibbyAgent/blob/88b82fb0cc421f230203ee93628325934465bf77/services/agent-service/src/utils/schemas.ts#L74
 */

const updateAgent = ({ agentId, payload }: TUpdateAgentData) =>
  HttpService.put<TAgent>(
    {
      path: pathBuilder(EServerPath.agent, { agentId: agentId || '' }),
      payload,
    },
    EHttpService.agent,
  )

const useUpdateAgent = (agentId?: string) => {
  const queryClient = useQueryClient()
  const userOrgId = store((state) => state.user.organizationId)

  return useMutation({
    mutationFn: (variables: TUpdateAgentData) =>
      updateAgent({
        ...variables,
        agentId: variables.agentId || agentId,
      }),
    onSuccess: (response, variables) => {
      const resolvedAgentId = variables.agentId || agentId
      const { hideToast, skipInvalidation } = variables

      if (!hideToast) {
        toast.dismiss()
        toast.success('Agent has been updated!')
      }

      if (skipInvalidation) {
        return
      }

      if (response?.data) {
        const newData = {
          ...response,
          data: getDefaultAgentData({
            ...(response.data || {}),
            scrapeStatus: variables.payload.agent?.scrapeStatus
              ? variables.payload.agent?.scrapeStatus
              : response.data?.[EAgentKeys.scrapeStatus] ||
                EWebScrapeStatus.unloaded,
          }),
        }
        const agentsByOrganizationData = queryClient.getQueryData(
          queryKeys.agentsByOrganization(response?.data?.orgId || ''),
        ) as HttpServiceBaseResponse<TAgent[]> | undefined

        if (agentsByOrganizationData) {
          queryClient.setQueryData(
            queryKeys.agentsByOrganization(response?.data?.orgId || ''),
            {
              ...agentsByOrganizationData,
              data: agentsByOrganizationData?.data?.map((agent: TAgent) => {
                if (agent.id === resolvedAgentId) {
                  return newData.data || agent
                }

                return agent
              }),
            },
          )
        }
      }

      queryClient.invalidateQueries({
        queryKey: queryKeys.agents({}),
        exact: false,
      })

      queryClient.invalidateQueries({
        queryKey: queryKeys.agentsByOrganization(response.data?.orgId || ''),
      })

      queryClient.invalidateQueries({
        queryKey: queryKeys.adminAgents({}),
      })

      if (variables.payload.agent?.phoneNumber) {
        queryClient.invalidateQueries({
          queryKey: queryKeys.agentPhoneNumber(resolvedAgentId || ''),
        })
      }

      const chatSettingsPayload = variables.payload[EAgentKeys.chatSettings]

      if (chatSettingsPayload && 'formId' in chatSettingsPayload) {
        queryClient.invalidateQueries({
          queryKey: preChatFormQueryKeys.preChatForms(
            response.data?.orgId || userOrgId || '',
          ),
        })
      }
    },
    onError: (_error, variables) => {
      const { hideErrorToast = false, skipInvalidation } = variables

      if (!hideErrorToast) {
        toast.dismiss()
        toast.error("Couldn't update Agent, please try again later.")
      }

      if (skipInvalidation) {
        return
      }

      queryClient.invalidateQueries({
        queryKey: queryKeys.agentsByOrganization(userOrgId || ''),
      })
    },
    retry: false,
  })
}

export default useUpdateAgent
